import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import type { PlaybookSection, DynamicFieldConfig, DropdownOption } from '../types';
import { useDropdownFields, useUpdateDropdownFields } from '../hooks/usePlaybook';
import { NoDataFound } from '@shared/components/NoDataFound/NoDataFound';
import InfoTooltip from '@shared/components/InfoTooltip';
import { LoadingSpinner, SearchField } from '@shared/components';
import { Module } from '@modules/admin/types';
import { usePermission } from '@modules/auth/hooks';

interface Props {
  section: PlaybookSection;
}

const nonEditableOptions = new Set(['a24', 'a178', 'a23134', 'a23134', 'a50639']);

const DropdownsTab: React.FC<Props> = ({ section }) => {
  const { canAdd, canUpdate } = usePermission(Module.PLAYBOOK);

  const moduleType = section.toLowerCase();

  // Fetch dropdown fields from API
  const { data: dropdownFields, isLoading, error } = useDropdownFields(moduleType);
  const updateMutation = useUpdateDropdownFields(moduleType);

  // Local state for managing edits
  const [localFields, setLocalFields] = useState<DynamicFieldConfig[]>([]);
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<DropdownOption | null>(null);
  const [form, setForm] = useState<{ label: string; value: string }>({ label: '', value: '' });
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize local state when data is loaded
  useEffect(() => {
    if (dropdownFields) {
      setLocalFields(dropdownFields);
      if (dropdownFields.length > 0 && !selectedFieldKey) {
        setSelectedFieldKey(dropdownFields[0].key);
      }
    }
  }, [dropdownFields]);

  const selectedField = localFields.find((f) => f.key === selectedFieldKey);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!selectedField?.options) return [];
    if (!searchQuery.trim()) return selectedField.options;
    const query = searchQuery.toLowerCase();
    return selectedField.options.filter(
      (option) => option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query),
    );
  }, [selectedField?.options, searchQuery]);

  const handleAddOption = () => {
    setEditingOption(null);
    setForm({ label: '', value: '' });
    setDialogOpen(true);
  };

  const handleEditOption = (option: DropdownOption) => {
    setEditingOption(option);
    setForm({ label: option.label, value: option.value });
    setDialogOpen(true);
  };

  const handleDeleteOption = async (option: DropdownOption) => {
    if (!selectedField) return;

    try {
      const currentOptions = selectedField.options || [];
      const updatedOptions = currentOptions.filter((opt) => opt.optionId !== option.optionId);

      const fieldPayload: DynamicFieldConfig = {
        ...selectedField,
        options: updatedOptions,
      };

      await updateMutation.mutateAsync([fieldPayload]);

      setLocalFields((prev) => prev.map((field) => (field.key === selectedFieldKey ? fieldPayload : field)));
    } catch (err) {
      console.error('Failed to delete option:', err);
    }
  };

  const handleDialogSubmit = async () => {
    if (!selectedField || !form.label || !form.value) return;

    try {
      const currentOptions = selectedField.options || [];

      // Prepare the option - include optionId only when editing
      let newOption: DropdownOption;
      if (editingOption) {
        newOption = { optionId: editingOption.optionId, label: form.label, value: form.value };
      } else {
        newOption = { label: form.label, value: form.value };
      }

      // Prepare updated options array
      let updatedOptions: DropdownOption[];
      if (editingOption) {
        updatedOptions = currentOptions.map((opt) => (opt.optionId === editingOption.optionId ? newOption : opt));
      } else {
        updatedOptions = [...currentOptions, newOption];
      }

      const fieldPayload: DynamicFieldConfig = {
        ...selectedField,
        options: updatedOptions,
      };

      await updateMutation.mutateAsync([fieldPayload]);

      setLocalFields((prev) => prev.map((field) => (field.key === selectedFieldKey ? fieldPayload : field)));

      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save option:', err);
      // Dialog stays open on error so user can retry
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading dropdown fields..." fullHeight />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load dropdown fields. Please try again.
      </Alert>
    );
  }

  if (!localFields || localFields.length === 0) {
    return <NoDataFound message="No dropdown fields configured for this module." py={8} />;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Dropdown Lists</Typography>
        <InfoTooltip title="Manage dropdown options used across forms. Select a field on the left, then add or edit options on the right. Changes are saved immediately." />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'stretch' }}>
        {/* Left Panel - Field List */}
        <Paper sx={{ width: { xs: '100%', md: 280 }, p: 1, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 1 }}>
            <Typography variant="subtitle1">Fields</Typography>
            <InfoTooltip title="Select which dropdown field you want to manage." />
          </Stack>
          <Divider />
          <List sx={{ py: 0 }}>
            {localFields.map((field) => (
              <ListItemButton
                key={field.key}
                selected={selectedFieldKey === field.key}
                onClick={() => setSelectedFieldKey(field.key)}
              >
                <ListItemText primary={field.label} secondary={`${(field.options || []).length} options`} />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Right Panel - Options List */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
          {selectedField ? (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {selectedField.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchField
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search options by label or value..."
                  />
                  {canAdd && (
                    <Button fullWidth size="small" variant="contained" startIcon={<Add />} onClick={handleAddOption}>
                      Add Option
                    </Button>
                  )}
                </Box>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {/* Search Bar */}

              {(() => {
                const hasOptions = (selectedField.options || []).length > 0;
                const hasFilteredResults = filteredOptions.length > 0;

                if (!hasOptions) {
                  return <NoDataFound message='No options added yet. Click "Add Option" to get started.' py={4} />;
                }

                if (!hasFilteredResults) {
                  return <NoDataFound message="No options match your search." py={4} />;
                }

                return (
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Label</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Value</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredOptions.map((option, index) => (
                          <TableRow key={option.optionId || `temp-${index}`} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{option.label}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                {option.value}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {canUpdate && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditOption(option)}
                                    disabled={nonEditableOptions.has(option.value)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </>
          ) : (
            <NoDataFound message="Select a field from the left to manage its options." py={4} />
          )}
        </Paper>
      </Stack>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography component="span" variant="h6" fontSize={18}>
              {editingOption ? 'Edit Option' : 'Add Option'}
            </Typography>
            <InfoTooltip title="Options are label/value pairs. Label is user-facing; value is stored in the database." />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Value"
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            disabled={!form.label || !form.value || updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {(() => {
              if (updateMutation.isPending) return 'Saving...';
              if (editingOption) return 'Update';
              return 'Add';
            })()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DropdownsTab;
